/**
 * Analytics Logger - Production-ready logging solution
 * 
 * OVERVIEW:
 * A production-ready logging solution that replaces console.log with intelligent analytics tracking.
 * Console logging is disabled by default - enable it manually when needed.
 * MQTT events are always sent regardless of console logging state.
 * 
 * FEATURES:
 * - Silent by Default: No console noise - console logging disabled by default
 * - MQTT Always Active: Events sent to MQTT regardless of console logging state
 * - Manual Console Toggle: Enable console logging only when needed via browser console
 * - Google Analytics Integration: Automatically sends errors to GA if available
 * - Performance Tracking: Built-in performance measurement
 * - Local Storage: Stores logs locally when console is enabled (dev only)
 * - Log Levels: DEBUG, INFO, WARN, ERROR, CRITICAL
 * - Buffering: Keeps last 50 logs in memory for debugging
 * 
 * DEFAULT BEHAVIOR:
 * - All Environments: Console logging disabled by default, MQTT events always sent
 * - Development/Test: Log level set to DEBUG
 * - Production: Log level set to WARN
 * - Logger exposed to window.analyticsLogger for manual control
 * 
 * USAGE EXAMPLES:
 * ```typescript
 * import { logger } from './tracking/analyticsLogger';
 * 
 * // Different log levels
 * logger.debug('Component', 'Rendering with props', props);
 * logger.info('User', 'Login successful', { userId: 123 });
 * logger.warn('API', 'Rate limit approaching', { remaining: 10 });
 * logger.error('Payment', 'Transaction failed', { error: err });
 * logger.critical('System', 'Database connection lost');
 * 
 * // Performance tracking
 * performance.mark('fetchData_start');
 * // ... your code ...
 * logger.trackPerformance('API', 'fetchData', elapsedTime);
 * 
 * // MQTT specific
 * logger.mqttConnection('connected');
 * logger.mqttReconnect(attempt, maxAttempts, delay);
 * logger.mqttMessage('sent', 'analytics.topic');
 * ```
 * 
 * BROWSER CONSOLE COMMANDS:
 * ```javascript
 * // Enable console logging
 * window.analyticsLogger.enableConsoleLogging()
 * window.analyticsLogger.toggleConsole()
 * 
 * // Enable production debugging
 * window.analyticsLogger.enableProductionDebug()
 * 
 * // View logs
 * window.analyticsLogger.getBuffer()           // Last 50 logs in memory
 * window.analyticsLogger.getStoredLogs()       // Last 100 logs from localStorage (dev only)
 * window.analyticsLogger.exportLogs()          // Export all logs as JSON
 * 
 * // Configuration
 * window.analyticsLogger.getConfig()           // Get current configuration
 * window.analyticsLogger.clearStoredLogs()     // Clear stored logs
 * ```
 * 
 * INTEGRATION:
 * - Google Analytics: ERROR and CRITICAL logs automatically sent to GA if window.gtag is available
 * - MQTT: All events sent to MQTT independently of console logging state
 * - Local Storage: Logs stored locally in non-production environments when console is enabled
 * 
 * BEST PRACTICES:
 * - Use appropriate log levels (DEBUG/INFO for dev, WARN/ERROR/CRITICAL for production)
 * - Include structured data objects instead of string concatenation
 * - Use consistent categories ('MQTT', 'Analytics', 'API', 'User', 'Performance')
 * - Track performance for critical operations
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4,
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: Record<string, any>;
  sessionId?: string;
}

/**
 * AnalyticsLogger Class
 * 
 * Manages logging with configurable levels, console output, and analytics integration.
 * Console logging is disabled by default to keep production console clean.
 * MQTT events are always sent regardless of console logging state.
 */
class AnalyticsLogger {
  private buffer: LogEntry[] = []; // In-memory buffer for last 50 logs
  private maxBufferSize = 50; // Maximum number of logs to keep in buffer
  private logLevel: LogLevel; // Current log level threshold
  private enableConsole: boolean; // Whether to output to console
  private enableLocalStorage: boolean; // Whether to store logs in localStorage (dev only)
  private forceDebugInProduction: boolean = false; // Flag to enable debugging in production

  constructor() {
    const isProduction = process.env.NODE_ENV === 'production';
    
    // Console logging disabled by default - only enabled via toggle function
    // This keeps console clean while MQTT events are still sent
    this.enableConsole = false;
    
    // Enable localStorage in non-production (for debugging when console is enabled)
    this.enableLocalStorage = !isProduction;
    
    // Set log level: DEBUG for non-production, WARN for production
    // Log level still applies even when console is disabled (for filtering)
    this.logLevel = isProduction ? LogLevel.WARN : LogLevel.DEBUG;
  }

  /**
   * Enable debugging in production (useful for troubleshooting)
   * 
   * Call from browser console: window.analyticsLogger.enableProductionDebug()
   * 
   * This will:
   * - Enable console logging
   * - Set log level to DEBUG
   * - Allow you to see all logs in production
   */
  enableProductionDebug(): void {
    this.forceDebugInProduction = true;
    this.enableConsole = true;
    this.logLevel = LogLevel.DEBUG;
    console.info('[Analytics Logger] Production debugging enabled');
  }

  /**
   * Disable debugging in production
   * 
   * Restores default production settings (console disabled, WARN level)
   */
  disableProductionDebug(): void {
    this.forceDebugInProduction = false;
    const isProduction = process.env.NODE_ENV === 'production';
    this.enableConsole = !isProduction;
    this.logLevel = isProduction ? LogLevel.WARN : LogLevel.DEBUG;
    console.info('[Analytics Logger] Production debugging disabled');
  }

  /**
   * Toggle console logging on/off
   * 
   * Useful for temporarily disabling logs without changing log level.
   * Returns the new state (true = enabled, false = disabled)
   * 
   * Usage: window.analyticsLogger.toggleConsole()
   */
  toggleConsole(): boolean {
    this.enableConsole = !this.enableConsole;
    console.info(`[Analytics Logger] Console logging ${this.enableConsole ? 'enabled' : 'disabled'}`);
    return this.enableConsole;
  }

  /**
   * Enable console logging
   * 
   * Usage: window.analyticsLogger.enableConsoleLogging()
   * Note: MQTT events are sent regardless of this setting
   */
  enableConsoleLogging(): void {
    this.enableConsole = true;
    console.info('[Analytics Logger] Console logging enabled');
  }

  /**
   * Disable console logging
   * 
   * Usage: window.analyticsLogger.disableConsoleLogging()
   * Note: MQTT events are still sent even when console is disabled
   */
  disableConsoleLogging(): void {
    this.enableConsole = false;
    console.info('[Analytics Logger] Console logging disabled');
  }

  /**
   * Check if a log level should be logged based on current log level threshold
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  /**
   * Create a log entry with timestamp and session ID
   */
  private createLogEntry(
    level: LogLevel,
    category: string,
    message: string,
    data?: Record<string, any>
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      sessionId: localStorage.getItem('sessionId') || undefined,
    };
  }

  /**
   * Send ERROR and CRITICAL logs to Google Analytics if available
   * 
   * Events are sent as: gtag('event', 'log_event', { event_category, event_label, value, custom_data })
   * Only ERROR and CRITICAL level logs are sent to GA
   */
  private sendToGoogleAnalytics(entry: LogEntry): void {
    // Send to Google Analytics if available
    if (window.gtag && entry.level >= LogLevel.ERROR) {
      try {
        window.gtag('event', 'log_event', {
          event_category: entry.category,
          event_label: entry.message,
          value: entry.level,
          custom_data: entry.data ? JSON.stringify(entry.data) : undefined,
        });
      } catch (e) {
        // Silently fail - don't want logging to break the app
      }
    }
  }

  /**
   * Store logs in localStorage for debugging (non-production only)
   * 
   * Keeps last 100 logs. Auto-rotates when limit is reached.
   * Only enabled in non-production environments.
   */
  private storeLocally(entry: LogEntry): void {
    if (!this.enableLocalStorage) return;

    try {
      const key = 'analytics_logs';
      const existing = localStorage.getItem(key);
      const logs = existing ? JSON.parse(existing) : [];
      logs.push(entry);
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.shift();
      }
      
      localStorage.setItem(key, JSON.stringify(logs));
    } catch (e) {
      // Storage might be full or disabled
    }
  }

  /**
   * Add log entry to in-memory buffer (last 50 logs)
   * 
   * Buffer is used for quick access via getBuffer() method.
   * Auto-rotates when maxBufferSize is reached.
   */
  private addToBuffer(entry: LogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift();
    }
  }

  /**
   * Core logging method - handles all log entries
   * 
   * Process:
   * 1. Check if log level meets threshold
   * 2. Create log entry with timestamp
   * 3. Add to in-memory buffer
   * 4. Output to console if enabled
   * 5. Store in localStorage if enabled (dev only)
   * 6. Send to Google Analytics if ERROR/CRITICAL
   * 
   * Note: MQTT events are handled separately and always sent regardless of console state
   */
  private log(level: LogLevel, category: string, message: string, data?: Record<string, any>): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, category, message, data);
    
    // Add to buffer for debugging
    this.addToBuffer(entry);
    
    // Output to console if enabled
    if (this.enableConsole) {
      const msg = `[${category}] ${message}`;
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(msg, data || '');
          break;
        case LogLevel.INFO:
          console.info(msg, data || '');
          break;
        case LogLevel.WARN:
          console.warn(msg, data || '');
          break;
        case LogLevel.ERROR:
        case LogLevel.CRITICAL:
          console.error(msg, data || '');
          break;
        default:
          console.log(msg, data || '');
      }
    }
    
    // Store locally for debugging (non-production only)
    this.storeLocally(entry);
    
    // Send to Google Analytics for errors (if available)
    if (entry.level >= LogLevel.ERROR) {
      this.sendToGoogleAnalytics(entry);
    }
  }

  /**
   * Public logging methods
   * 
   * Usage:
   * - logger.debug('Component', 'Rendering', { props })     // Detailed debugging (dev only)
   * - logger.info('User', 'Action completed', { userId })   // General information (dev only)
   * - logger.warn('API', 'Rate limit approaching', data)    // Warning conditions (production)
   * - logger.error('Payment', 'Transaction failed', error)  // Error conditions (production + GA)
   * - logger.critical('System', 'Database down')           // Critical failures (production + GA)
   * 
   * Best Practices:
   * - Use structured data objects instead of string concatenation
   * - Use consistent categories ('MQTT', 'Analytics', 'API', 'User', 'Performance')
   * - DEBUG/INFO won't show in production unless production debug is enabled
   */
  debug(category: string, message: string, data?: Record<string, any>): void {
    this.log(LogLevel.DEBUG, category, message, data);
  }

  info(category: string, message: string, data?: Record<string, any>): void {
    this.log(LogLevel.INFO, category, message, data);
  }

  warn(category: string, message: string, data?: Record<string, any>): void {
    this.log(LogLevel.WARN, category, message, data);
  }

  error(category: string, message: string, data?: Record<string, any>): void {
    this.log(LogLevel.ERROR, category, message, data);
  }

  critical(category: string, message: string, data?: Record<string, any>): void {
    this.log(LogLevel.CRITICAL, category, message, data);
  }

  /**
   * Track performance metrics
   * 
   * Usage:
   * ```typescript
   * performance.mark('fetchData_start');
   * // ... your code ...
   * const elapsed = performance.now() - startTime;
   * logger.trackPerformance('API', 'fetchData', elapsed);
   * ```
   * 
   * Automatically sends timing data to Google Analytics if available.
   */
  trackPerformance(category: string, metricName: string, value: number): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    // Use Performance API
    if (window.performance && typeof window.performance.measure === 'function') {
      try {
        performance.mark(`${metricName}_end`);
        performance.measure(metricName, `${metricName}_start`, `${metricName}_end`);
      } catch (e) {
        // Marks might not exist
      }
    }

    // Send to Google Analytics if available
    if (window.gtag) {
      try {
        window.gtag('event', 'timing_complete', {
          name: metricName,
          value: Math.round(value),
          event_category: category,
        });
      } catch (e) {
        // Silently fail
      }
    }

    this.info(category, `Performance: ${metricName}`, { value });
  }

  /**
   * MQTT-specific logging methods
   * 
   * These methods are used for MQTT connection and message tracking.
   * Note: MQTT events are always sent regardless of console logging state.
   */
  mqttConnection(status: 'connected' | 'disconnected' | 'error', details?: Record<string, any>): void {
    const level = status === 'error' ? LogLevel.ERROR : LogLevel.INFO;
    this.log(level, 'MQTT', `Connection ${status}`, details);
  }

  mqttReconnect(attempt: number, maxAttempts: number, delay: number): void {
    this.info('MQTT', `Reconnection attempt ${attempt}/${maxAttempts}`, { delay });
  }

  mqttMessage(action: 'sent' | 'queued' | 'dropped', topic: string): void {
    this.debug('MQTT', `Message ${action}`, { topic });
  }

  /**
   * Get buffered logs for debugging
   * 
   * Returns last 50 logs from in-memory buffer.
   * Usage: window.analyticsLogger.getBuffer()
   */
  getBuffer(): LogEntry[] {
    return [...this.buffer];
  }

  /**
   * Get logs from localStorage
   * 
   * Returns last 100 logs stored in localStorage (dev only).
   * Usage: window.analyticsLogger.getStoredLogs()
   */
  getStoredLogs(): LogEntry[] {
    if (!this.enableLocalStorage) return [];
    
    try {
      const logs = localStorage.getItem('analytics_logs');
      return logs ? JSON.parse(logs) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear stored logs
   * 
   * Clears both localStorage logs and in-memory buffer.
   * Usage: window.analyticsLogger.clearStoredLogs()
   */
  clearStoredLogs(): void {
    localStorage.removeItem('analytics_logs');
    this.buffer = [];
  }

  /**
   * Export logs as JSON (for debugging)
   * 
   * Returns all logs (buffer + stored) as formatted JSON string.
   * Usage: window.analyticsLogger.exportLogs()
   */
  exportLogs(): string {
    const logs = {
      buffer: this.buffer,
      stored: this.getStoredLogs(),
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      productionDebugEnabled: this.forceDebugInProduction,
    };
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Get current configuration
   * 
   * Returns current logger settings including:
   * - enableConsole: Whether console logging is enabled
   * - enableLocalStorage: Whether localStorage logging is enabled
   * - logLevel: Current log level threshold
   * - productionDebugEnabled: Whether production debug is enabled
   * - environment: Current environment (development/production)
   * 
   * Usage: window.analyticsLogger.getConfig()
   */
  getConfig(): {
    enableConsole: boolean;
    enableLocalStorage: boolean;
    logLevel: string;
    productionDebugEnabled: boolean;
    environment: string;
  } {
    return {
      enableConsole: this.enableConsole,
      enableLocalStorage: this.enableLocalStorage,
      logLevel: LogLevel[this.logLevel],
      productionDebugEnabled: this.forceDebugInProduction,
      environment: process.env.NODE_ENV || 'unknown',
    };
  }
}

/**
 * Singleton instance - use this for all logging
 * 
 * Import: import { logger } from './tracking/analyticsLogger';
 * 
 * Example:
 * ```typescript
 * logger.info('Component', 'Rendered successfully');
 * logger.error('API', 'Request failed', { error: err.message });
 * ```
 */
export const logger = new AnalyticsLogger();

/**
 * Window interface for browser console debugging
 * 
 * The logger is always exposed to window.analyticsLogger for debugging.
 * This allows you to control logging from browser console even in production.
 */
declare global {
  interface Window {
    analyticsLogger?: AnalyticsLogger;
    gtag?: (command: string, eventName: string, parameters: any) => void;
  }
}

// Expose to window for debugging (always available)
if (typeof window !== 'undefined') {
  window.analyticsLogger = logger;
}